import React, { useEffect, useState, useCallback, useRef } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import StatsBoard from "../../../components/StatsBoard";
import API from "../../../utils/api";
import classes from "./StatsDetail.module.css";
import LoaderComp from "../../../components/LoaderComp";

const formatDuration = (ms = 0) => {
  if (!ms || Number.isNaN(ms)) return "--";
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${mins}m ${remainingSeconds}s`;
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
};

const formatPakistanDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    const pakistanDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Karachi" })
    );
    const day = String(pakistanDate.getDate()).padStart(2, "0");
    const month = String(pakistanDate.getMonth() + 1).padStart(2, "0");
    const year = pakistanDate.getFullYear();
    let hours = pakistanDate.getHours();
    const minutes = String(pakistanDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, "0");
    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch {
    return "N/A";
  }
};

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function AdminStatsDetail() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [currentUser, setCurrentUser] = useState(readUser());
  const isInitialMount = useRef(true);

  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    const handleUserUpdate = () => setCurrentUser(readUser());
    window.addEventListener("userUpdated", handleUserUpdate);
    return () => window.removeEventListener("userUpdated", handleUserUpdate);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken") || "";
      const response = await fetch(`${API.base}/users.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ action: "fetch" }),
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setUsers(data);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]);

  const fetchData = async (targetUserId = null) => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("authToken") || "";
      // Use targetUserId if provided, otherwise use selectedUserId, but convert empty string to null
      const userId =
        targetUserId !== null ? targetUserId : selectedUserId || null;

      const statsParams = new URLSearchParams();
      if (isAdmin && userId) {
        statsParams.append("user_id", userId);
      }

      const logsParams = new URLSearchParams({ action: "list", limit: 25 });
      if (isAdmin && userId) {
        logsParams.append("user_id", userId);
      }

      const [statsRes, logsRes] = await Promise.all([
        fetch(`${API.base}/site_stats.php?${statsParams.toString()}`, {
          headers: { Authorization: token },
        }),
        fetch(`${API.base}/import_logs.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: logsParams,
        }),
      ]);

      const statsText = await statsRes.text();
      let statsJson;
      try {
        statsJson = JSON.parse(statsText);
      } catch (parseErr) {
        console.error("Failed to parse stats JSON:", statsText);
        throw new Error(
          "Invalid response from server. Please check server logs."
        );
      }

      if (!statsRes.ok || !statsJson.success) {
        throw new Error(statsJson.error || "Failed to load stats");
      }

      const logsText = await logsRes.text();
      let logsJson;
      try {
        logsJson = JSON.parse(logsText);
      } catch (parseErr) {
        console.error("Failed to parse logs JSON:", logsText);
        throw new Error(
          "Invalid response from server. Please check server logs."
        );
      }

      if (!logsRes.ok || !logsJson.success) {
        throw new Error(logsJson.error || "Failed to load import logs");
      }

      setStats(statsJson);
      setLogs(logsJson.logs || []);
    } catch (err) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on initial load and when user selection changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Initial load - fetch current user's data
      fetchData(null);
    } else {
      // Refetch data whenever selectedUserId changes
      // Empty string means current user, otherwise it's a specific user ID
      fetchData(selectedUserId || null);
    }
  }, [selectedUserId]);

  return (
    <DashboardLayout>
      <div className={classes.page}>
        {loading ? (
          <LoaderComp />
        ) : error ? (
          <div className={classes.errorCard}>
            <p>{error}</p>
            <button type="button" onClick={fetchData}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {isAdmin && (
              <div className={classes.userSelectorCard}>
                <div className={classes.userSelectorHeader}>
                  <p className={classes.kicker}>Admin Control</p>
                  <h3>View User Details</h3>
                  <p>Select a user to view their specific data</p>
                </div>
                <div className={classes.userSelector}>
                  <label htmlFor="userSelect">Select User:</label>
                  <select
                    id="userSelect"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className={classes.userSelect}
                  >
                    <option value="">Current User (Me)</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email}) - {user.role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <StatsBoard
              counts={stats?.counts}
              importSummary={stats?.import_summary}
              recentRuns={stats?.recent_runs}
              generatedAt={stats?.generated_at}
              title="Admin Data Detail"
              subtitle="Full visibility into catalog imports and volumes."
            />

            <section className={classes.detailGrid}>
              <article className={classes.detailCard}>
                <div className={classes.detailHead}>
                  <div>
                    <p className={classes.kicker}>Manual entries</p>
                    <h3>Latest Product</h3>
                  </div>
                  <span className={classes.chip}>
                    {stats?.user_manual_count ?? 0} total
                  </span>
                </div>
                {stats?.user_manual_product ? (
                  <ul className={classes.detailList}>
                    <li>
                      <span>SKU</span>
                      <strong>{stats.user_manual_product.sku || "—"}</strong>
                    </li>
                    <li>
                      <span>Name</span>
                      <strong>
                        {stats.user_manual_product.productName || "—"}
                      </strong>
                    </li>
                    <li>
                      <span>Status</span>
                      <strong className={classes.badge}>
                        {stats.user_manual_product.status || "draft"}
                      </strong>
                    </li>
                    <li>
                      <span>Created</span>
                      <strong>
                        {formatDateTime(stats.user_manual_product.created_at)}
                      </strong>
                    </li>
                    <li>
                      <span>Via</span>
                      <strong>
                        {stats.user_manual_product.created_via || "manual"}
                      </strong>
                    </li>
                  </ul>
                ) : (
                  <div className={classes.emptyState}>
                    No products added from the Add Product page yet.
                  </div>
                )}
              </article>

              <article className={classes.detailCard}>
                <div className={classes.detailHead}>
                  <div>
                    <p className={classes.kicker}>Signed-in user</p>
                    <h3>User Snapshot</h3>
                  </div>
                </div>
                {stats?.user_profile ? (
                  <ul className={classes.detailList}>
                    <li>
                      <span>Name</span>
                      <strong>{stats.user_profile.name || "—"}</strong>
                    </li>
                    <li>
                      <span>Email</span>
                      <strong>{stats.user_profile.email || "—"}</strong>
                    </li>
                    <li>
                      <span>Role</span>
                      <strong>
                        {(stats.user_profile.role || "user").toUpperCase()}
                      </strong>
                    </li>
                    <li>
                      <span>Phone</span>
                      <strong>{stats.user_profile.phone || "—"}</strong>
                    </li>
                    <li>
                      <span>Manual adds</span>
                      <strong>{stats?.user_manual_count ?? 0}</strong>
                    </li>
                  </ul>
                ) : (
                  <div className={classes.emptyState}>
                    User information is currently unavailable.
                  </div>
                )}
              </article>
            </section>

            {stats?.user_manual_products &&
              stats.user_manual_products.length > 0 && (
                <section className={classes.logsCard}>
                  <header>
                    <div>
                      <p className={classes.kicker}>Manual entries</p>
                      <h3>All Manual Products</h3>
                      <p>Total {stats.user_manual_products.length} entries</p>
                    </div>
                  </header>
                  <div className={classes.tableWrap}>
                    <table>
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Status</th>
                          <th>Via</th>
                          <th>Name</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.user_manual_products.map((product) => (
                          <tr key={product.id}>
                            <td>
                              <code className={classes.skuCode}>
                                {product.sku || "—"}
                              </code>
                            </td>
                            <td>
                              <span className={classes.badge}>
                                {product.status || "draft"}
                              </span>
                            </td>
                            <td>{product.created_via || "manual"}</td>
                            <td>{product.productName || "—"}</td>
                            <td>
                              {formatPakistanDateTime(product.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

            <section className={classes.logsCard}>
              <header>
                <div>
                  <p className={classes.kicker}>Audit</p>
                  <h3>Import activity log</h3>
                  <p>Last {logs.length} entries (backend)</p>
                </div>
              </header>
              {logs.length === 0 ? (
                <div className={classes.emptyState}>
                  No import logs recorded.
                </div>
              ) : (
                <div className={classes.tableWrap}>
                  <table>
                    <thead>
                      <tr>
                        <th>Completed</th>
                        <th>Processed</th>
                        <th>Imported</th>
                        <th>Skipped</th>
                        <th>Duration</th>
                        <th>Owner</th>
                        <th>File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            {log.finished_at
                              ? new Date(log.finished_at).toLocaleString()
                              : "—"}
                          </td>
                          <td>{log.rows_total}</td>
                          <td>{log.rows_imported}</td>
                          <td>{log.rows_skipped}</td>
                          <td>{formatDuration(log.duration_ms)}</td>
                          <td>{log.user_name || log.user_email || "—"}</td>
                          <td>{log.file_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
